/**
 * Custom validation messages.
 *
 * The forms artboard's headline is "Every error message contains a fact": a
 * message should name what is wrong and what would be right, not say that
 * something is wrong. The browser already knows WHEN a field is invalid — the
 * required flag, the type, the pattern — so only the wording is ours, and it
 * comes from the copy deck through a per-field setting.
 *
 * setCustomValidity has one trap: a non-empty message makes the field
 * permanently invalid until it is cleared. So it is cleared on every input and
 * re-applied only at the moment the browser reports the field invalid.
 */
(() => {
  const SELECTOR = '[data-dopa-invalid], [data-dopa-required]';
  const fields = document.querySelectorAll(SELECTOR);
  if (!fields.length) return;

  /*
   * The board draws the message under the field, not in the browser's bubble:
   * a 15px circled "!" in the error colour, stroke 1.7, 7px from 13.5px text,
   * and a 2px error rule on the field itself. The browser still decides WHEN
   * the field is invalid; the bubble is suppressed and this row shown instead.
   */
  const ICON = '<svg class="dopa-form__icon" width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="7.2"/><path d="M10 6.4v4.4M10 13.4v.6"/></svg>';
  const show = (field, text) => {
    const row = field.closest('.dopa-form__row');
    if (!row || !text) return;
    let msg = row.querySelector('.dopa-form__field-error');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'dopa-form__field-error';
      msg.id = field.id + '-error';
      msg.innerHTML = ICON + '<span></span>';
      (field.closest('.dopa-form__prefixed') || field).insertAdjacentElement('afterend', msg);
    }
    msg.querySelector('span').textContent = text;
    msg.hidden = false;
    row.classList.add('is-invalid');
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', msg.id);
  };
  const hide = (field) => {
    const row = field.closest('.dopa-form__row');
    const msg = row && row.querySelector('.dopa-form__field-error');
    if (msg) msg.hidden = true;
    if (row) row.classList.remove('is-invalid');
    field.removeAttribute('aria-invalid');
  };

  for (const field of fields) {
    // Clear first, or the field stays invalid with stale text.
    const clear = () => { field.setCustomValidity(''); hide(field); };
    field.addEventListener('input', clear);
    field.addEventListener('change', clear);

    field.addEventListener('invalid', (event) => {
      event.preventDefault();
      /*
       * An empty field and a badly-shaped one are different facts and get
       * different lines. Asking someone to check the format of a value they
       * have not typed yet is noise, and "this one we need" is wrong once
       * there is something in the box.
       *
       * Anything that is not valueMissing — typeMismatch, patternMismatch,
       * tooShort — is a shape problem and takes the format line. Where a
       * field has no format line, setCustomValidity('') leaves the browser's
       * own wording, which is better than a message that does not fit.
       */
      const missing = field.validity.valueMissing;
      const message = missing
        ? field.getAttribute('data-dopa-required')
        : field.getAttribute('data-dopa-invalid');
      field.setCustomValidity(message || '');
      show(field, message || field.validationMessage);
    });
  }

  // The message is only correct while it describes the current value, so it is
  // dropped again the moment the form is submitted successfully.
  for (const form of document.querySelectorAll('.dopa-form')) {
    form.addEventListener('submit', () => {
      for (const field of form.querySelectorAll(SELECTOR)) field.setCustomValidity('');
    });
  }
})();

/**
 * The sample request's line picker.
 *
 * Every row ships in the markup; this hides the empty ones after the first and
 * reveals one per "Add a line", so the form degrades to three visible rows
 * rather than to one row with no way to add a second. The format list is
 * filled from the chosen product's own variants, and "Any" stays first because
 * it is always an honest answer.
 *
 * On send, the chosen lines are kept in this tab's session storage so the
 * success state can list back what was asked for — Shopify's contact form
 * returns nothing it was sent.
 */
(() => {
  const KEY = 'dopa-sample-echo';

  for (const box of document.querySelectorAll('[data-dopa-lines]')) {
    const rows = [...box.querySelectorAll('[data-dopa-line]')];
    const add = box.querySelector('[data-dopa-line-add]');
    const note = box.querySelector('[data-dopa-line-limit]');
    const limit = Number(box.dataset.limit) || rows.length;

    const product = (row) => row.querySelector('[data-dopa-line-product]');
    const pack = (row) => row.querySelector('[data-dopa-line-pack]');

    /* Each option already names its pack ("Thai Red Curry Paste, 195 g Jar"),
       so the FORMAT WANTED column repeats it as text rather than asking twice. */
    const fillFormats = (row) => {
      const chosen = product(row).selectedOptions[0];
      pack(row).textContent = chosen && chosen.value ? chosen.dataset.pack || '' : '';
    };

    const sync = () => {
      const shown = rows.filter((r) => !r.hidden).length;
      add.hidden = shown >= limit;
      note.hidden = shown < limit;
      rows.forEach((r, i) => {
        r.querySelector('[data-dopa-line-remove]').hidden = shown === 1 && i === 0;
      });
    };

    rows.forEach((row, i) => {
      if (i > 0 && !product(row).value) row.hidden = true;
      product(row).addEventListener('change', () => fillFormats(row));
      row.querySelector('[data-dopa-line-remove]').addEventListener('click', () => {
        product(row).value = '';
        fillFormats(row);
        if (rows.filter((r) => !r.hidden).length > 1) row.hidden = true;
        sync();
      });
    });

    add.addEventListener('click', () => {
      const next = rows.find((r) => r.hidden);
      if (next) { next.hidden = false; product(next).focus(); }
      sync();
    });
    sync();

    const form = box.closest('form');
    if (form) {
      form.addEventListener('submit', () => {
        const lines = rows
          .filter((r) => !r.hidden && product(r).value)
          .map((r) => ({
            name: product(r).value,
            format: '',
            state: product(r).selectedOptions[0].dataset.state || '',
          }));
        try { sessionStorage.setItem(KEY, JSON.stringify(lines)); } catch (e) { /* storage blocked: no echo */ }
      });
    }
  }

  const echo = document.querySelector('[data-dopa-sample-echo]');
  if (echo) {
    let lines = [];
    try { lines = JSON.parse(sessionStorage.getItem(KEY) || '[]'); } catch (e) { lines = []; }
    if (lines.length) {
      for (const line of lines) {
        const li = document.createElement('li');
        const name = document.createElement('span');
        name.textContent = line.format ? `${line.name} · ${line.format}` : line.name;
        li.append(name);
        if (line.state) {
          const state = document.createElement('span');
          state.className = 'dopa-form__echo-state';
          state.textContent = line.state;
          li.append(state);
        }
        echo.append(li);
      }
      echo.hidden = false;
      try { sessionStorage.removeItem(KEY); } catch (e) { /* nothing to clear */ }
    }
  }
})();

/*
 * The sample request, arriving from a shelf that filtered to nothing.
 *
 * collection.html draws the empty state and says what happens next: "THE
 * SAMPLE FORM ARRIVES PRE-FILLED WITH THE FILTERS THAT FAILED — CUISINE,
 * CATEGORY, DIETARY AND FORMAT — SO NOBODY RETYPES WHAT THEY JUST CLICKED."
 *
 * The link carries both the filter parameters and, in looking_for, the labels
 * the visitor actually clicked. Liquid cannot read a query parameter back, so
 * the writing happens here. It only ever fills an EMPTY message box, and it
 * never touches anything a person has typed.
 */
(function () {
  const params = new URLSearchParams(window.location.search);
  const looking = (params.get('looking_for') || '').trim();
  if (!looking) return;
  const box = document.querySelector('form textarea[name="contact[Message]"], form textarea');
  if (!box || box.value.trim()) return;
  const prefix = box.getAttribute('data-dopa-looking-prefix') || 'Looking for:';
  box.value = prefix + ' ' + looking;
  /* Mark it so the styling can show it was carried, and so a later pass can
     find it. The visitor can overwrite every word of it. */
  box.setAttribute('data-dopa-prefilled', 'true');
})();
